import React, { useState, useEffect } from 'react';
import VoicePlayer from '../VoicePlayer';

const STATUS_STYLES = {
  done:    { border: '#107c10', bg: '#f0faf0', iconBg: '#107c10', badgeBg: '#dff6dd', badgeColor: '#107c10' },
  active:  { border: '#009245', bg: '#F2F8E9', iconBg: '#009245', badgeBg: '#EAF3DE', badgeColor: '#009245' },
  pending: { border: '#e1dfdd', bg: '#ffffff', iconBg: '#c8c6c4', badgeBg: '', badgeColor: '' },
  noted:   { border: '#ffaa44', bg: '#fff8f0', iconBg: '#ffaa44', badgeBg: '#fff4ce', badgeColor: '#7a5c00' },
};

/**
 * Turns URLs and protegrity.com addresses inside plain text into clickable
 * links that open in a new tab. Skips email addresses (preceded by @).
 */
const URL_RE = /(https?:\/\/[^\s<>()]+|(?<![@\w.])(?:[a-z0-9-]+\.)+protegrity\.com(?:\/[^\s<>()]*)?)/gi;

function linkify(text) {
  if (typeof text !== 'string') return text;
  const parts = text.split(URL_RE);
  return parts.map((part, i) => {
    if (!part || !URL_RE.test(part)) {
      URL_RE.lastIndex = 0;
      return part;
    }
    URL_RE.lastIndex = 0;
    // Keep trailing punctuation outside the link
    const m = part.match(/[.,;:!?]+$/);
    const tail = m ? m[0] : '';
    const clean = tail ? part.slice(0, -tail.length) : part;
    const href = clean.startsWith('http') ? clean : `https://${clean}`;
    return (
      <React.Fragment key={i}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          style={{ color: '#009245', fontWeight: 600, textDecoration: 'underline' }}
        >
          {clean}
        </a>
        {tail}
      </React.Fragment>
    );
  });
}

export default function ChecklistSection({ section, sectionNumber, totalSections, status, os, onComplete, onNote, onSkip }) {
  const [open, setOpen] = useState(status === 'active');
  const [stepChecked, setStepChecked] = useState({});
  const [noted, setNoted] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);

  useEffect(() => {
    if (status === 'active') {
      setOpen(true);
    } else if (status === 'done') {
      setOpen(false);
    }
  }, [status]);

  const st = STATUS_STYLES[noted ? 'noted' : status] || STATUS_STYLES.pending;
  const isPending = status === 'pending';
  const isDone = status === 'done';

  // Use new steps structure if available, fall back to old items
  const allSteps = section.steps || [];
  const visibleSteps = allSteps.filter(step => step.os === 'all' || step.os === os);
  const checkedCount = visibleSteps.filter(step => stepChecked[step.id]).length;

  function toggleStep(id) {
    if (isPending || isDone) return;
    setStepChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function handleNote() {
    if (!noteText.trim()) return;
    setNoted(true);
    setShowNoteInput(false);
    onNote(section.id, section.title, noteText);
  }

  function handleComplete() {
    if (status !== 'active') return;
    onComplete(section.id);
  }

  function handleSkip() {
    if (status !== 'active') return;
    onSkip(section.id);
  }

  const iconContent = isDone ? '✓' : noted ? '!' : section.icon || sectionNumber;
  const badgeText = isDone ? 'Covered'
    : noted ? 'Noted for follow-up'
    : status === 'active' ? 'Now covering'
    : 'Upcoming';

  return (
    <div style={{
      background: st.bg,
      border: `${status === 'active' ? '2px' : '1px'} solid ${st.border}`,
      borderRadius: 8,
      overflow: 'hidden',
      opacity: isPending && !open ? 0.6 : 1,
      transition: 'all 0.3s',
      boxShadow: status === 'active' ? '0 3px 12px rgba(98,100,167,0.18)' : 'none',
    }}>
      {/* SECTION HEADER */}
      <div
        onClick={() => setOpen(o => !o)}
        title={isPending ? 'Preview this section — progress is not affected' : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: 11,
          padding: '13px 14px', cursor: 'pointer',
          userSelect: 'none',
          background: open && status === 'active' ? 'rgba(98,100,167,0.06)' : 'transparent',
        }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: st.iconBg, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 19, fontWeight: 700, flexShrink: 0,
        }}>
          {iconContent}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 17.5, fontWeight: 700, color: '#201f1e',
            display: 'flex', alignItems: 'center', gap: 7,
          }}>
            <span style={{ color: '#8a8886', fontSize: 14, fontWeight: 500 }}>
              {sectionNumber}/{totalSections}
            </span>
            <span>{section.title}</span>
            {section.titleSuffix && section.titleSuffix[os] && (
              <span style={{ fontWeight: 600, color: '#009245', fontSize: 16 }}>
                ({section.titleSuffix[os]})
              </span>
            )}
            {section.optional && (
              <span style={{ fontWeight: 400, color: '#8a8886', fontSize: 12.5 }}>(optional)</span>
            )}
          </div>
          {status === 'active' && visibleSteps.length > 0 && (
            <div style={{ fontSize: 14, color: '#605e5c', marginTop: 3, fontWeight: 500 }}>
              {checkedCount} of {visibleSteps.length} steps completed
            </div>
          )}
        </div>

        {badgeText && (
          <span style={{
            fontSize: 12.5, padding: '4px 9px', borderRadius: 10, fontWeight: 600,
            background: isPending ? '#edebe9' : st.badgeBg,
            color: isPending ? '#605e5c' : st.badgeColor,
            whiteSpace: 'nowrap',
          }}>
            {isPending && open ? 'Preview' : badgeText}
          </span>
        )}

        <span style={{
          fontSize: 19, color: '#999', marginLeft: 2,
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s', display: 'inline-block', fontWeight: 700,
        }}>›</span>
      </div>

      {/* SECTION BODY */}
      {open && (
        <div style={{
          padding: '0 14px 14px',
          borderTop: status === 'active' ? `1px solid ${st.border}40` : 'none',
        }}>
          {/* Preview notice — upcoming section opened for reference only */}
          {isPending && (
            <div style={{
              background: '#e1f3ff', border: '1px solid #b8daff',
              borderRadius: 5, padding: '8px 11px', marginTop: 12,
              fontSize: 13.5, color: '#005a9e',
            }}>
              👀 Preview only — this section is coming up later. Nothing here is
              recorded until it becomes the current section.
            </div>
          )}
          {/* AGENT VOICE SCRIPT */}
          {section.agentScript && (
            <div style={{
              background: 'rgba(98,100,167,0.12)',
              borderLeft: '3px solid #009245',
              padding: '10px 12px',
              fontSize: 15,
              color: '#3b3b6e',
              borderRadius: 4,
              marginTop: 12,
              marginBottom: 12,
              lineHeight: 1.5,
            }}>
              <div style={{
                fontSize: 12.5, fontWeight: 700, color: '#009245',
                marginBottom: 4, letterSpacing: 0.5,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                🎙️ AGENT SAYS
                <span style={{
                  background: '#009245', color: '#fff',
                  padding: '2px 7px', borderRadius: 8,
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                }}>
                  🔊 VOICE
                </span>
              </div>
              <div style={{ fontStyle: 'italic' }}>
                "{section.agentScript}"
            </div>
              <VoicePlayer section={section} os={os} />
            <div style={{ display: 'none' }}>
              </div>
            </div>
          )}

          {/* MAIN LINK — prominent */}
          {section.mainLink && (
            <a
              href={section.mainLink.url}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 9,
                background: '#fff', border: '2px solid #009245',
                borderRadius: 6, padding: '10px 13px', textDecoration: 'none',
                marginBottom: 14, transition: 'all 0.15s',
                fontWeight: 700,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F2F8E9'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
            >
              <span style={{ fontSize: 20 }}>🔗</span>
              <span style={{ fontSize: 15.5, color: '#009245', flex: 1 }}>
                {section.mainLink.label}
              </span>
              <span style={{ fontSize: 17.5, color: '#009245', fontWeight: 700 }}>→</span>
            </a>
          )}

          {/* STEP-BY-STEP INSTRUCTIONS */}
          {visibleSteps.length > 0 && (
            <div style={{
              background: '#f0fdfa',
              border: '1px solid #9FE1CB',
              borderRadius: 6,
              padding: '10px 12px',
              marginBottom: 12,
            }}>
              <div style={{
                fontSize: 14.5, fontWeight: 700, color: '#0F6E56',
                marginBottom: 8, letterSpacing: 0.3, textTransform: 'uppercase',
              }}>
                📋 Step-by-step instructions
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {visibleSteps.map((step, idx) => {
                  const checked = stepChecked[step.id];
                  return (
                    <div
                      key={step.id}
                      onClick={() => { if (!isPending) toggleStep(step.id); }}
                      style={{
                        background: checked ? '#f0faf0' : '#fff',
                        border: `1.5px solid ${checked ? '#107c10' : '#9FE1CB'}`,
                        borderRadius: 6,
                        padding: '10px 12px',
                        display: 'flex', gap: 11, alignItems: 'flex-start',
                        cursor: isDone || isPending ? 'default' : 'pointer',
                        transition: 'all 0.2s',
                        opacity: checked ? 0.8 : 1,
                      }}
                      onMouseEnter={e => { if (!isDone && !isPending && !checked) { e.currentTarget.style.borderColor = '#1D9E75'; e.currentTarget.style.background = '#f0fdfa'; } }}
                      onMouseLeave={e => { if (!checked) { e.currentTarget.style.borderColor = '#9FE1CB'; e.currentTarget.style.background = '#fff'; } }}
                    >
                      {/* Step number */}
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: checked ? '#107c10' : '#0F6E56',
                        color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 15.5, fontWeight: 700, flexShrink: 0,
                      }}>
                        {checked ? '✓' : (idx + 1)}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: 15.5, fontWeight: 600, color: '#201f1e',
                          textDecoration: checked ? 'line-through' : 'none',
                          opacity: checked ? 0.7 : 1, lineHeight: 1.4,
                        }}>
                          {step.text}
                        </div>
                        {step.detail && (
                          <div style={{
                            fontSize: 14.5, color: '#605e5c', marginTop: 4,
                            lineHeight: 1.45,
                            opacity: checked ? 0.7 : 1,
                          }}>
                            {linkify(step.detail)}
                          </div>
                        )}

                        {/* Labelled action links attached to a step */}
                        {step.stepLinks && step.stepLinks.length > 0 && (
                          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {step.stepLinks.map((l, i) => (
                              <a
                                key={i}
                                href={l.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 7,
                                  alignSelf: 'flex-start',
                                  background: '#EAF3DE', border: '1px solid #C0DD97',
                                  borderRadius: 5, padding: '7px 12px',
                                  fontSize: 14, fontWeight: 600, color: '#009245',
                                  textDecoration: 'none',
                                }}
                              >
                                🔗 {l.label} <span style={{ fontWeight: 700 }}>→</span>
                              </a>
                            ))}
                          </div>
                        )}

                        {/* RULES box for steps with password requirements etc */}
                        {step.rules && step.rules.length > 0 && (
                          <div style={{
                            background: '#fff8e8',
                            border: '1px solid #f5d490',
                            borderRadius: 5,
                            padding: '8px 10px',
                            marginTop: 6,
                          }}>
                            <div style={{
                              fontSize: 12.5, fontWeight: 700, color: '#8a6c00',
                              marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3,
                            }}>
                              {step.rulesLabel || '⚠️ Must meet ALL these rules:'}
                            </div>
                            <ul style={{
                              fontSize: 14, color: '#5a4500', lineHeight: 1.6,
                              marginLeft: 18, marginTop: 0, marginBottom: 0,
                            }}>
                              {step.rules.map((rule, i) => (
                                <li key={i}>{rule}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* IMPORTANT NOTES */}
          {section.notes && section.notes.length > 0 && (
            <div style={{
              background: '#fff8e8',
              border: '1px solid #f5d490',
              borderRadius: 6,
              padding: '10px 12px',
              marginBottom: 10,
            }}>
              <div style={{
                fontSize: 14, fontWeight: 700, color: '#8a6c00',
                marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3,
              }}>
                📌 Important to remember
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {section.notes.map((note, i) => (
                  <div key={i} style={{
                    fontSize: 14.5, color: '#5a4500', lineHeight: 1.5,
                  }}>
                    {linkify(note)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ADDITIONAL LINKS */}
          {section.links && section.links.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
              {section.links.filter(l => !l.os || l.os === 'all' || l.os === os).map(link => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: '#fff', border: '1.5px solid #c8c6c4',
                    borderRadius: 5, padding: '8px 11px', textDecoration: 'none',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#009245'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#c8c6c4'; }}
                >
                  <span style={{ fontSize: 15 }}>📎</span>
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#605e5c', flex: 1 }}>
                    {link.label}
                  </span>
                  <span style={{ fontSize: 14, color: '#8a8886' }}>→</span>
                </a>
              ))}
            </div>
          )}

          {/* NOTE display */}
          {noted && (
            <div style={{
              background: '#fff4ce', border: '1.5px solid #f0c389',
              borderRadius: 5, padding: '9px 11px', marginBottom: 8,
              fontSize: 14.5, color: '#7a5c00', lineHeight: 1.4,
            }}>
              <div style={{ fontWeight: 700, color: '#d97a00', marginBottom: 3 }}>📝 Noted for follow-up</div>
              {noteText}
            </div>
          )}

          {/* NOTE input */}
          {showNoteInput && !noted && (
            <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea
                placeholder="Briefly describe what to follow up on..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                autoFocus
                style={{
                  width: '100%', padding: '8px 11px', fontSize: 15,
                  border: '1.5px solid #ffaa44', borderRadius: 5, resize: 'none',
                  fontFamily: 'Segoe UI, sans-serif', height: 70,
                  outline: 'none', color: '#201f1e', boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={handleNote}
                  disabled={!noteText.trim()}
                  style={{
                    flex: 1, padding: '8px 0', fontSize: 15, fontWeight: 600,
                    background: noteText.trim() ? '#ffaa44' : '#c8c6c4',
                    color: '#fff', border: 'none',
                    borderRadius: 5, cursor: noteText.trim() ? 'pointer' : 'default',
                    fontFamily: 'inherit',
                  }}
                >
                  Save note
                </button>
                <button
                  onClick={() => { setShowNoteInput(false); setNoteText(''); }}
                  style={{
                    flex: 1, padding: '8px 0', fontSize: 15,
                    background: '#fff', color: '#605e5c',
                    border: '1.5px solid #c8c6c4', borderRadius: 5,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ACTION BUTTONS */}
          {status === 'active' && !showNoteInput && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 7,
              paddingTop: 10, borderTop: '1px solid rgba(98,100,167,0.15)', marginTop: 6,
            }}>
              <button
                onClick={handleComplete}
                style={{
                  width: '100%', padding: '12px 0', fontSize: 16, fontWeight: 700,
                  background: '#107c10', color: '#fff', border: 'none',
                  borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#0e6b0e'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#107c10'; }}
              >
                ✓ Mark as Covered — Next Section
              </button>

              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => setShowNoteInput(true)}
                  style={{
                    flex: 1, padding: '8px 0', fontSize: 14, fontWeight: 600,
                    background: '#fff', color: '#d97a00',
                    border: '1.5px solid #ffaa44', borderRadius: 5,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  📝 Add follow-up note
                </button>

                {section.optional && (
                  <button
                    onClick={handleSkip}
                    style={{
                      flex: 1, padding: '8px 0', fontSize: 14, fontWeight: 600,
                      background: '#fff', color: '#605e5c',
                      border: '1.5px solid #c8c6c4', borderRadius: 5,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    ⏭️ Skip — email later
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
